import React, { useState, useEffect } from 'react';
import { Star, X } from 'lucide-react';
import { Button } from './button';
import { Textarea } from './textarea';
import { Label } from './label';
import { apiService } from '../../services/apiService';
import { toast } from 'sonner';

const RatingForm = ({ 
  clubId, 
  clubName, 
  existingRating = null, 
  onSuccess, 
  onCancel,
  isOpen = false 
}) => {
  const [rating, setRating] = useState(existingRating?.rating || 0);
  const [hoverRating, setHoverRating] = useState(0);
  const [reviewText, setReviewText] = useState(existingRating?.review_text || '');
  const [isAnonymous, setIsAnonymous] = useState(existingRating?.is_anonymous || false);
  const [categoryRatings, setCategoryRatings] = useState({
    community: existingRating?.community_rating || 0,
    organization: existingRating?.organization_rating || 0,
    difficulty: existingRating?.difficulty_rating || 0,
    fun_factor: existingRating?.fun_factor_rating || 0
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (existingRating) {
      setRating(existingRating.rating);
      setReviewText(existingRating.review_text || '');
      setIsAnonymous(existingRating.is_anonymous);
      setCategoryRatings({
        community: existingRating.community_rating || 0,
        organization: existingRating.organization_rating || 0,
        difficulty: existingRating.difficulty_rating || 0,
        fun_factor: existingRating.fun_factor_rating || 0
      });
    }
  }, [existingRating]);

  const handleStarClick = (starRating) => {
    setRating(starRating);
  };

  const handleStarHover = (starRating) => {
    setHoverRating(starRating);
  };

  const handleStarLeave = () => {
    setHoverRating(0);
  };

  const handleCategoryRating = (category, starRating) => {
    setCategoryRatings(prev => ({
      ...prev,
      [category]: starRating
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (rating === 0) {
      toast.error('Please select a rating');
      return;
    }

    setLoading(true);
    
    try {
      const ratingData = {
        club_id: clubId,
        rating: rating,
        review_text: reviewText.trim() || null,
        is_anonymous: isAnonymous,
        community_rating: categoryRatings.community || null,
        organization_rating: categoryRatings.organization || null,
        difficulty_rating: categoryRatings.difficulty || null,
        fun_factor_rating: categoryRatings.fun_factor || null
      };

      if (existingRating) {
        // Update existing rating - remove club_id from update data
        const updateData = { ...ratingData };
        delete updateData.club_id;
        await apiService.clubRatings.updateRating(existingRating.id, updateData);
        toast.success('Rating updated successfully!');
      } else {
        // Create new rating
        await apiService.clubRatings.createRating(ratingData);
        toast.success('Rating submitted successfully!');
      }
      
      onSuccess?.();
    } catch (error) {
      console.error('Error submitting rating:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit rating');
    } finally {
      setLoading(false);
    }
  };

  const renderStars = (currentRating, onStarClick, onStarHover, onStarLeave, size = "w-5 h-5") => {
    const stars = [];
    const displayRating = hoverRating || currentRating;
    
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <button
          key={i}
          type="button"
          className="focus:outline-none"
          onClick={() => onStarClick(i)}
          onMouseEnter={() => onStarHover(i)}
          onMouseLeave={onStarLeave}
        >
          <Star 
            className={`${size} ${
              i <= displayRating 
                ? 'fill-yellow-400 text-yellow-400' 
                : 'text-gray-300'
            } transition-colors`} 
          />
        </button>
      );
    }
    
    return stars;
  };

  const categories = [
    { key: 'community', label: 'Community', description: 'How welcoming and friendly the group is' },
    { key: 'organization', label: 'Organization', description: 'How well the runs are planned and managed' },
    { key: 'difficulty', label: 'Difficulty Level', description: 'How well the pace matches your level' },
    { key: 'fun_factor', label: 'Fun Factor', description: 'How enjoyable the overall experience is' }
  ];

  return (
    <div className={`fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 ${!isOpen ? 'hidden' : ''}`}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900" style={{fontFamily: 'Montserrat, sans-serif'}}>
                {existingRating ? 'Update Rating' : 'Rate Club'}
              </h2>
              <p className="text-gray-600 mt-1" style={{fontFamily: 'Montserrat, sans-serif'}}>
                {clubName}
              </p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={onCancel}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Overall Rating */}
            <div>
              <Label className="text-base font-semibold text-gray-900 mb-3 block" style={{fontFamily: 'Montserrat, sans-serif'}}>
                Overall Rating *
              </Label>
              <div className="flex items-center gap-1">
                {renderStars(rating, handleStarClick, handleStarHover, handleStarLeave)}
                <span className="ml-2 text-sm text-gray-600">
                  {rating > 0 ? `${rating} star${rating !== 1 ? 's' : ''}` : 'Select rating'}
                </span>
              </div>
            </div>

            {/* Category Ratings */}
            <div>
              <Label className="text-base font-semibold text-gray-900 mb-3 block" style={{fontFamily: 'Montserrat, sans-serif'}}>
                Category Ratings (Optional)
              </Label>
              <div className="space-y-4">
                {categories.map((category) => (
                  <div key={category.key} className="space-y-2">
                    <div>
                      <Label className="text-sm font-medium text-gray-700" style={{fontFamily: 'Montserrat, sans-serif'}}>
                        {category.label}
                      </Label>
                      <p className="text-xs text-gray-500" style={{fontFamily: 'Montserrat, sans-serif'}}>
                        {category.description}
                      </p>
                    </div>
                    <div className="flex items-center gap-1">
                      {renderStars(
                        categoryRatings[category.key], 
                        (rating) => handleCategoryRating(category.key, rating),
                        (rating) => handleCategoryRating(category.key, rating),
                        () => {},
                        "w-4 h-4"
                      )}
                      <span className="ml-2 text-xs text-gray-600">
                        {categoryRatings[category.key] > 0 ? `${categoryRatings[category.key]} star${categoryRatings[category.key] !== 1 ? 's' : ''}` : 'Optional'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Review Text */}
            <div>
              <Label htmlFor="review" className="text-base font-semibold text-gray-900 mb-3 block" style={{fontFamily: 'Montserrat, sans-serif'}}>
                Describe your experience (Optional)
              </Label>
              <Textarea
                id="review"
                value={reviewText}
                onChange={(e) => setReviewText(e.target.value)}
                placeholder="A few kind words..."
                className="min-h-[100px] resize-none bg-white text-gray-900 border-gray-300"
                maxLength={500}
              />
              <p className="text-xs text-gray-500 mt-1" style={{fontFamily: 'Montserrat, sans-serif'}}>
                {reviewText.length}/500 characters
              </p>
            </div>


            {/* Submit Buttons */}
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={onCancel}
                className="flex-1 border-gray-300 text-gray-700 hover:bg-gray-50"
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-black text-white hover:bg-gray-800"
                disabled={loading || rating === 0}
              >
                {loading ? 'Submitting...' : (existingRating ? 'Update Rating' : 'Submit Rating')}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default RatingForm;
